import React, { useContext, useState } from "react";
import { InfoContext, Match } from "../../../../InfoContext";
import { AuthContext } from "../../../../AuthContext";
import { useAlert } from "../../../../AlertContext";
import { UserContext } from "../../../../UserContext";
import MatchDate from "./MatchDate";
import ProposedResult from "./ProposedResult";
import SuggestResultForm from "./SuggestResultForm";
import ContactInfo from "./ContactInfo";
import ScorerPopup from "./ScorerPopup";
import axios from "axios";

const server_url = process.env.REACT_APP_BACKEND_URL;

interface MatchCardProps {
  match: Match;
  own_team_id: number;
  is_ref: boolean;
  is_ref2?: boolean;
  for_ref: boolean;
}

const MatchCard: React.FC<MatchCardProps> = ({
  match,
  own_team_id,
  is_ref,
  is_ref2,
  for_ref = false,
}) => {
  const { getTeamById, fetchSeasons, getPlayerByIdAndTeam } =
    useContext(InfoContext);
  const { user } = useContext(UserContext);
  const { session, role } = useContext(AuthContext);
  const { showAlert } = useAlert();
  const [loadingAcceptResult, setLoadingAcceptResult] = useState(false);

  const [showFinalScorers, setShowFinalScorers] = useState(false);
  const [showFinalScorerPopup, setShowFinalScorerPopup] = useState(false);

  // --- Referee selection state (first + second) ---------------------------
  const [showRefereeSelect, setShowRefereeSelect] = useState(false);
  const [selectedRefereeId, setSelectedRefereeId] = useState<number | null>(
    null
  );

  const [showRefereeSelect2, setShowRefereeSelect2] = useState(false);
  const [selectedRefereeId2, setSelectedRefereeId2] = useState<number | null>(
    null
  );

  // --- Teams & players ----------------------------------------------------
  const homeTeam = getTeamById(match.home_id);
  const guestTeam = getTeamById(match.guest_id);
  const ownTeam = getTeamById(own_team_id);
  const ownTeamPlayers = ownTeam?.players ? [...ownTeam.players] : [];
  const ownTeamPlayerIds = ownTeamPlayers.map((p) => p.id);

  const enemy_captain =
    own_team_id === homeTeam?.team_id ? guestTeam?.captain : homeTeam?.captain;

  // First referee ---------------------------------------------
  const refereeTeam = getTeamById(match.referee_team_id);
  const referee = getPlayerByIdAndTeam(match.referee_team_id, match.referee_id);
  const refereeTeamPlayers = refereeTeam?.players
    ? [...refereeTeam.players]
    : [];
  if (
    refereeTeam?.captain &&
    !refereeTeamPlayers.some((p) => p.id === refereeTeam.captain?.id)
  ) {
    refereeTeamPlayers.push(refereeTeam.captain);
  }

  // Second referee --------------------------------------------
  const refereeTeam2 = getTeamById(match.referee_team_id_2);
  const referee2 = getPlayerByIdAndTeam(
    match.referee_team_id_2,
    match.referee_id_2
  );
  const refereeTeamPlayers2 = refereeTeam2?.players
    ? [...refereeTeam2.players]
    : [];
  if (
    refereeTeam2?.captain &&
    !refereeTeamPlayers2.some((p) => p.id === refereeTeam2.captain?.id)
  ) {
    refereeTeamPlayers2.push(refereeTeam2.captain);
  }

  // Results & permissions -------------------------------------
  const final_result = match.results.find((result) => result.is_final);
  const my_result = match.results.find(
    (result) => result.submitted_by === user?.id
  );

  const ownTeamIsHome = own_team_id === match.home_id;
  const allowedGoalsFinal =
    final_result &&
    (ownTeamIsHome ? final_result.score_home : final_result.score_guest);

  const finalScorersForOwnTeam =
    final_result?.scorer.filter((s) => s.team_id === own_team_id) ?? [];

  const isDateEditable =
    (final_result && role === "ADMIN") ||
    (!final_result &&
      (role === "ADMIN" ||
        (role === "CAPTAIN" &&
          (own_team_id === match.home_id || own_team_id === match.guest_id))));

  // --- Helper -------------------------------------------------------------
  const post = async (url: string, payload: Record<string, any>) =>
    axios.post(url, { access_token: session.access_token, ...payload });

  const handleAssignReferee = async () => {
    if (!selectedRefereeId) return;
    try {
      const { data } = await post(`${server_url}/assignReferee`, {
        match_id: match.id,
        referee_id: selectedRefereeId,
      });
      if (data.status) {
        showAlert("success", "Referee assigned!");
        setShowRefereeSelect(false);
        setSelectedRefereeId(null);
        await fetchSeasons();
      } else {
        showAlert("error", data.error);
      }
    } catch {
      showAlert("error", "Error assigning referee");
    }
  };

  const handleAssignReferee2 = async () => {
    if (!selectedRefereeId2) return;
    try {
      const { data } = await post(`${server_url}/assignReferee`, {
        match_id: match.id,
        referee_id: selectedRefereeId2,
      });
      if (data.status) {
        showAlert("success", "Referee (2) assigned!");
        setShowRefereeSelect2(false);
        setSelectedRefereeId2(null);
        await fetchSeasons();
      } else {
        showAlert("error", data.error);
      }
    } catch {
      showAlert("error", "Error assigning referee (2)");
    }
  };

  // ---------------------- RENDER (VIEWER) -------------------------------
  if (role !== "ADMIN" && role !== "CAPTAIN") {
    return (
      <div className="p-2 rounded-xl mt-2">
        {referee && referee.id === user?.id && (
          <p className="font-bold text-lg">
            ❗❗You are referee for this game❗❗
          </p>
        )}
        <p className="text-lg font-semibold mb-2">
          {homeTeam?.name} : {guestTeam?.name}
        </p>
        {!for_ref ? (
          <ContactInfo
            label="Enemy Captain:"
            name={enemy_captain?.name}
            phone={enemy_captain?.phone}
          />
        ) : (
          <>
            <ContactInfo
              label={`Captain ${homeTeam?.name_short}:`}
              name={homeTeam?.captain?.name}
              phone={homeTeam?.captain?.phone}
            />
            <ContactInfo
              label={`Captain ${guestTeam?.name_short}:`}
              name={guestTeam?.captain?.name}
              phone={guestTeam?.captain?.phone}
            />
          </>
        )}
        <p className="mt-2">
          Referee Team: {refereeTeam ? refereeTeam.name : "tbd"}
        </p>
        {referee2 && referee2.id === user?.id && (
          <p className="font-bold text-lg">
            ❗❗You are second referee for this game❗❗
          </p>
        )}
        <p>Referee Team 2: {refereeTeam2 ? refereeTeam2.name : "tbd"}</p>

        <p className="my-2">
          Date:{" "}
          {match.date
            ? new Date(match.date).toLocaleString("de-DE", {
                timeZone: "UTC",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "tbd"}
        </p>
        {final_result && (
          <p className="text-lg font-semibold">
            Final Result: {final_result.score_home} : {final_result.score_guest}
          </p>
        )}
      </div>
    );
  }

  // ---------------------- RENDER (ADMIN / CAPTAIN) ----------------------
  return (
    <div className="p-2 rounded-xl mt-2">
      {referee && referee.id === user?.id && (
        <p className="font-bold text-lg">
          ❗❗You are referee for this game❗❗
        </p>
      )}
      <p className="text-lg font-semibold mb-2">
        {homeTeam?.name} : {guestTeam?.name}
      </p>

      {!for_ref ? (
        <ContactInfo
          label="Enemy Captain:"
          name={enemy_captain?.name}
          phone={enemy_captain?.phone}
        />
      ) : (
        <>
          <ContactInfo
            label={`Captain ${homeTeam?.name_short}:`}
            name={homeTeam?.captain?.name}
            phone={homeTeam?.captain?.phone}
          />
          <ContactInfo
            label={`Captain ${guestTeam?.name_short}:`}
            name={guestTeam?.captain?.name}
            phone={guestTeam?.captain?.phone}
          />
        </>
      )}

      <p className="mt-2">
        Referee Team: {refereeTeam ? refereeTeam.name : "tbd"}
      </p>
      <ContactInfo
        label={`Captain ${refereeTeam?.name_short}:`}
        name={refereeTeam?.captain?.name}
        phone={refereeTeam?.captain?.phone}
      />
      {/* First referee row */}
      <div className="relative mb-2">
        <ContactInfo
          label="Referee:"
          name={referee?.name}
          phone={referee?.phone}
        />
        {((is_ref && role === "CAPTAIN") || role === "ADMIN") && (
          <button
            onClick={() => setShowRefereeSelect(!showRefereeSelect)}
            className="py-1 px-3 ml-2 rounded-xl bg-blue-500 hover:bg-blue-600"
          >
            Select Referee
          </button>
        )}
      </div>

      {showRefereeSelect && (
        <div className="flex flex-wrap gap-3 mb-4 items-end">
          <select
            value={selectedRefereeId || ""}
            onChange={(e) =>
              setSelectedRefereeId(e.target.value ? +e.target.value : null)
            }
            className="w-60 px-3 py-2 border rounded-xl text-black"
          >
            <option value="">-- Choose player --</option>
            {refereeTeamPlayers.map((player: any) => (
              <option key={player.id} value={player.id}>
                {player.name}
              </option>
            ))}
          </select>
          <button
            onClick={handleAssignReferee}
            className="py-2 px-3 rounded-xl bg-green-500 hover:bg-green-600"
          >
            Save
          </button>
          <button
            onClick={() => {
              setShowRefereeSelect(false);
              setSelectedRefereeId(null);
            }}
            className="py-2 px-3 rounded-xl bg-gray-500 hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Second referee row */}
      {referee2 && referee2.id === user?.id && (
        <p className="font-bold text-lg">
          ❗❗You are second referee for this game❗❗
        </p>
      )}
      <p>Referee Team 2: {refereeTeam2 ? refereeTeam2.name : "tbd"}</p>

      <ContactInfo
        label={`Captain ${refereeTeam2?.name_short}:`}
        name={refereeTeam2?.captain?.name}
        phone={refereeTeam2?.captain?.phone}
      />
      <div className="relative mb-2">
        <ContactInfo
          label="Referee 2:"
          name={referee2?.name}
          phone={referee2?.phone}
        />
        {((is_ref2 && role === "CAPTAIN") || role === "ADMIN") && (
          <button
            onClick={() => setShowRefereeSelect2(!showRefereeSelect2)}
            className="py-1 px-3 ml-2 rounded-xl bg-blue-500 hover:bg-blue-600"
          >
            Select Referee 2
          </button>
        )}
      </div>

      {showRefereeSelect2 && (
        <div className="flex flex-wrap gap-3 mb-4 items-end">
          <select
            value={selectedRefereeId2 || ""}
            onChange={(e) =>
              setSelectedRefereeId2(e.target.value ? +e.target.value : null)
            }
            className="w-60 px-3 py-2 border rounded-xl text-black"
          >
            <option value="">-- Choose player --</option>
            {refereeTeamPlayers2.map((player: any) => (
              <option key={player.id} value={player.id}>
                {player.name}
              </option>
            ))}
          </select>
          <button
            onClick={handleAssignReferee2}
            className="py-2 px-3 rounded-xl bg-green-500 hover:bg-green-600"
          >
            Save
          </button>
          <button
            onClick={() => {
              setShowRefereeSelect2(false);
              setSelectedRefereeId2(null);
            }}
            className="py-2 px-3 rounded-xl bg-gray-500 hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Date --------------------------------------------------- */}
      <MatchDate
        date={match.date}
        isEditable={isDateEditable}
        onUpdate={async (newDate) => {
          try {
            const { data } = await post(`${server_url}/updateMatchDate`, {
              match_id: match.id,
              date: newDate,
            });
            if (data.status) {
              showAlert("success", "Date updated!");
              await fetchSeasons();
            } else {
              showAlert("error", data.error);
            }
          } catch {
            showAlert("error", "Error updating date");
          }
        }}
      />

      {/* ─────────── Final Result + Scorer-Toggle ─────────── */}
      {final_result ? (
        <>
          <div className="flex items-center">
            <p className="text-lg font-semibold">
              Final Result: {final_result.score_home} :{" "}
              {final_result.score_guest}
            </p>
            <button
              onClick={() => setShowFinalScorers(!showFinalScorers)}
              className="ml-2 text-blue-500"
              style={{
                transition: "transform 0.3s ease",
                transform: showFinalScorers ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              ▼
            </button>
          </div>

          {showFinalScorers && (
            <div className="mt-2 ml-4">
              {finalScorersForOwnTeam.length > 0 ? (
                finalScorersForOwnTeam.map((s) => {
                  const player = getPlayerByIdAndTeam(s.team_id, s.user_id);
                  return (
                    <p key={s.id}>
                      {player?.name || "Player"}: {s.goal_count}
                    </p>
                  );
                })
              ) : (
                <p>No scorers recorded.</p>
              )}

              {(allowedGoalsFinal || 0) > 0 && (
                <button
                  onClick={() => setShowFinalScorerPopup(true)}
                  className="mt-2 py-2 px-4 rounded-xl bg-blue-500 hover:bg-blue-600 text-white"
                >
                  Edit Scorer
                </button>
              )}
            </div>
          )}

          {showFinalScorerPopup && (
            <ScorerPopup
              teamId={own_team_id}
              matchId={match.id}
              resultId={final_result.id}
              allowedGoals={allowedGoalsFinal || 0}
              initialGoals={finalScorersForOwnTeam}
              onClose={() => setShowFinalScorerPopup(false)}
            />
          )}
        </>
      ) : (
        /* ---------------- Vorschläge / Suggest-Form ---------------- */
        !is_ref && (
          <>
            {match.results.map((result) => (
              <ProposedResult
                key={result.id}
                result={result}
                isMyResult={(ownTeamPlayerIds.includes(result.submitted_by) || result.submitted_by === user?.id)}
                isAdmin={role === "ADMIN"}
                onAccept={async (resultId) => {
                  try {
                    setLoadingAcceptResult(true);
                    const { data } = await post(`${server_url}/acceptResult`, {
                      result_id: resultId,
                    });
                    if (data.status) {
                      showAlert("success", "Result accepted!");
                      await fetchSeasons();
                    } else {
                      showAlert("error", data.error);
                    }
                  } catch {
                    showAlert(
                      "error",
                      "Unexpected Error when accepting Result!"
                    );
                  } finally {
                    setLoadingAcceptResult(false);
                  }
                }}
                loadingAcceptResult={loadingAcceptResult}
                ownTeamId={own_team_id}
                matchId={match.id}
                homeTeamId={match.home_id}
                guestTeamId={match.guest_id}
              />
            ))}

            {!my_result &&
              (own_team_id === homeTeam?.team_id ||
                own_team_id === guestTeam?.team_id ||
                role === "ADMIN") && (
                <SuggestResultForm
                  matchId={match.id}
                  homeTeamName={homeTeam?.name_short || "Home"}
                  guestTeamName={guestTeam?.name_short || "Guest"}
                  onSubmit={async (scoreHome, scoreGuest) => {
                    try {
                      const { data } = await post(`${server_url}/addResult`, {
                        match_id: match.id,
                        home_score: scoreHome,
                        guest_score: scoreGuest,
                      });
                      if (data.status) {
                        showAlert("success", "Result added!");
                        await fetchSeasons();
                      } else {
                        showAlert("error", "Error adding Result");
                      }
                    } catch {
                      showAlert("error", "Error adding Result");
                    }
                  }}
                />
              )}
          </>
        )
      )}
    </div>
  );
};

export default MatchCard;
